/**
 * Allowlist sanitizer for ICON SVG MARKUP — the search-bar quick-filter chip
 * icons (#871, PR #1415 direction change, Eli 2026-09-07). Sibling of
 * ./content-html and ./inline-html, which guard operator HTML; this one guards
 * a much smaller vocabulary (path-shaped SVG) and, unlike those two, REJECTS
 * the whole input instead of stripping, because an icon with parts removed is
 * a wrong icon, not a degraded one.
 *
 * Why it exists: the dashboard picker fetches the chosen icon from Iconify
 * ONCE, at selection time, and stores the markup in the chip config as
 * `iconSvg`. The widget then renders that stored markup inline, so the
 * customer page never contacts api.iconify.design (same reasoning as #1423's
 * static Lucide subset in widgets/amenities/iconResolver.ts). Stored markup
 * is admin-controlled data reaching a `dangerouslySetInnerHTML` sink on every
 * visitor's page, so it goes through this sanitizer at SAVE time (dashboard)
 * and AGAIN at RENDER time (widget) — the client is not a security boundary,
 * a compromised dashboard account is the injection source, and re-running a
 * 16 KB tokenizer per chip is cheap.
 *
 * Pure string processing on purpose — a hand-rolled tokenizer, no DOMParser,
 * no dependency — so the SAME function runs in the widget's `renderToString`
 * SSR path, the browser mount, the dashboard, and plain Node tests.
 *
 * Policy (fail closed — any deviation returns null):
 *  - Elements: only ICON_SVG_ALLOWED_ELEMENTS, case-sensitive; one root
 *    `<svg>`, never nested. Anything else (`script`, `foreignObject`, `style`,
 *    `image`, `a`, `iframe`, `mask`, `text`, `linearGradient`, …) rejects.
 *  - Attributes: only ICON_SVG_ALLOWED_ATTRIBUTES survive; unknown ones
 *    (`style`, `aria-*`, `data-*`, `xmlns:xlink`, …) are dropped. Before the
 *    allowlist runs, EVERY attribute — kept or not — is checked and the input
 *    rejected on: a name starting with `on`, any `javascript:`/`vbscript:`/
 *    `data:` value (entity-decoded, whitespace and control characters
 *    stripped), any backslash (CSS escape), any `url(` that is not a local
 *    `#id` reference, and any `href`/`xlink:href` that is not a local `#id`
 *    on a `<use>`.
 *  - Structure: XML declaration and comments are skipped; DOCTYPE, CDATA and
 *    processing instructions reject; text is whitespace-only outside
 *    `<title>`; nesting is capped; total input is capped at
 *    MAX_ICON_SVG_LENGTH.
 *
 * Output mirrors the shape iconResolver.ts already stores: `markup` is the
 * root's inner markup with no `<svg>` wrapper, `viewBox` is normalized, and
 * `svg` is the canonical wrapped form the dashboard persists. Sanitizing
 * `svg` again yields the same result (idempotent), which is what lets the
 * widget re-run this on the stored value.
 *
 * `idPrefix` scopes `id="…"` and every local `#id` reference (href, url(#…))
 * so two inlined icons that both use `id="a"` for a clipPath cannot cross-
 * reference each other on the same page.
 */
import { decodeEntities } from './content-html'

export const MAX_ICON_SVG_LENGTH = 16 * 1024
export const MAX_ICON_SVG_DEPTH = 32
export const MAX_ICON_SVG_ELEMENTS = 512
export const MAX_ICON_SVG_ATTRIBUTES_PER_ELEMENT = 32

export const ICON_SVG_ALLOWED_ELEMENTS = [
  'svg', 'g', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon',
  'defs', 'clipPath', 'use', 'title',
] as const

export const ICON_SVG_ALLOWED_ATTRIBUTES = [
  'viewBox', 'width', 'height', 'd',
  'fill', 'fill-rule', 'fill-opacity', 'clip-rule', 'clip-path',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-opacity', 'stroke-dasharray',
  'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points',
  'transform', 'opacity', 'id', 'class', 'xmlns',
] as const

export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

export interface SanitizedIconSvg {
  /** Normalized `minX minY width height`, space separated. */
  viewBox: string
  /** Inner markup of the root `<svg>` — same shape as iconResolver.ts paths. */
  markup: string
  /** Canonical wrapped form: `<svg xmlns viewBox>markup</svg>`. Store this. */
  svg: string
}

export interface SanitizeIconSvgOptions {
  /** Prepended to every `id` and local `#id` reference. Non `[A-Za-z0-9_-]` characters are dropped. */
  idPrefix?: string
}

const ALLOWED_ELEMENTS = new Set<string>(ICON_SVG_ALLOWED_ELEMENTS)
const ALLOWED_ATTRIBUTES = new Set<string>(ICON_SVG_ALLOWED_ATTRIBUTES)

const NAME_START = /[A-Za-z_]/
const NAME_CHAR = /[A-Za-z0-9_.:-]/
const LOCAL_ID_REF = /^#[A-Za-z_][A-Za-z0-9_.:-]*$/
const NUMBER_TOKEN = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/
// Text allowed inside <title>: plain characters or well-formed references only.
const TITLE_TEXT = /^(?:[^<>&]|&(?:amp|lt|gt|quot|apos|#\d{1,7}|#x[0-9a-fA-F]{1,6});)*$/
const DANGEROUS_SCHEME = /(?:javascript|vbscript|livescript|data):/
const CONTROL_AND_WHITESPACE = /[\u0000-\u0020\u007f-\u00a0\u2000-\u200f\u2028\u2029\u3000\ufeff]/g

interface ParsedElement {
  name: string
  attributes: Array<[string, string]> // kept attributes, decoded values
  children: Array<ParsedElement | string> // strings are validated title text
}

class Reject extends Error {}

function reject(): never {
  throw new Reject()
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Rejects on every hostile attribute shape regardless of whether the
 * attribute would be kept. Runs on the entity-DECODED value, since that is
 * what a browser resolves.
 */
function assertAttributeSafe(element: string, name: string, decoded: string): void {
  const lowerName = name.toLowerCase()
  if (lowerName.startsWith('on')) reject()
  if (decoded.includes('\\')) reject()

  const stripped = decoded.replace(CONTROL_AND_WHITESPACE, '').toLowerCase()
  if (DANGEROUS_SCHEME.test(stripped)) reject()

  // Every url( must be a local reference: url(#id), url('#id') or url("#id").
  const opens = stripped.split('url(').length - 1
  if (opens > 0) {
    const matches = stripped.match(/url\(([^)]*)\)/g) ?? []
    if (matches.length !== opens) reject()
    for (const m of matches) {
      const inner = m.slice(4, -1).replace(/^['"]|['"]$/g, '')
      if (!LOCAL_ID_REF.test(inner)) reject()
    }
  }

  if (lowerName === 'href' || lowerName.endsWith(':href')) {
    if (element !== 'use') reject()
    if (!LOCAL_ID_REF.test(decoded.trim())) reject()
  }
}

function normalizeViewBox(value: string): string | null {
  const parts = value.trim().split(/[\s,]+/)
  if (parts.length !== 4 || !parts.every((p) => NUMBER_TOKEN.test(p) && Number.isFinite(Number(p)))) return null
  if (Number(parts[2]) <= 0 || Number(parts[3]) <= 0) return null
  return parts.join(' ')
}

class Tokenizer {
  private i = 0
  private elementCount = 0

  constructor(private readonly s: string) {}

  private peek(offset = 0): string {
    return this.s[this.i + offset] ?? ''
  }

  private startsWith(lit: string): boolean {
    return this.s.startsWith(lit, this.i)
  }

  private skipWhitespace(): void {
    while (this.i < this.s.length && /\s/.test(this.s[this.i])) this.i++
  }

  /** Skips whitespace and comments; rejects DOCTYPE/CDATA/PI in that position. */
  private skipBetween(): void {
    for (;;) {
      this.skipWhitespace()
      if (this.startsWith('<!--')) {
        const end = this.s.indexOf('-->', this.i + 4)
        if (end < 0) reject()
        this.i = end + 3
        continue
      }
      return
    }
  }

  private readName(): string {
    const start = this.i
    if (!NAME_START.test(this.peek())) reject()
    this.i++
    while (this.i < this.s.length && NAME_CHAR.test(this.s[this.i])) this.i++
    return this.s.slice(start, this.i)
  }

  private readQuoted(): string {
    const quote = this.peek()
    if (quote !== '"' && quote !== "'") reject()
    this.i++
    const end = this.s.indexOf(quote, this.i)
    if (end < 0) reject()
    const raw = this.s.slice(this.i, end)
    if (raw.includes('<')) reject()
    this.i = end + 1
    return raw
  }

  parseDocument(): ParsedElement {
    this.skipWhitespace()
    if (this.startsWith('<?xml')) {
      const end = this.s.indexOf('?>', this.i)
      if (end < 0) reject()
      this.i = end + 2
    }
    this.skipBetween()
    if (this.peek() !== '<') reject()
    const root = this.parseElement(0)
    if (root.name !== 'svg') reject()
    this.skipBetween()
    if (this.i !== this.s.length) reject()
    return root
  }

  private parseElement(depth: number): ParsedElement {
    if (depth > MAX_ICON_SVG_DEPTH) reject()
    if (++this.elementCount > MAX_ICON_SVG_ELEMENTS) reject()
    // at '<'
    this.i++
    const name = this.readName()
    if (!ALLOWED_ELEMENTS.has(name)) reject()
    if (name === 'svg' && depth !== 0) reject()

    const attributes: Array<[string, string]> = []
    let sawHref = false
    let attrCount = 0
    let selfClosing = false
    for (;;) {
      this.skipWhitespace()
      if (this.startsWith('/>')) {
        this.i += 2
        selfClosing = true
        break
      }
      if (this.peek() === '>') {
        this.i++
        break
      }
      if (++attrCount > MAX_ICON_SVG_ATTRIBUTES_PER_ELEMENT) reject()
      const attrName = this.readName()
      this.skipWhitespace()
      if (this.peek() !== '=') reject()
      this.i++
      this.skipWhitespace()
      const raw = this.readQuoted()
      const decoded = decodeEntities(raw)
      assertAttributeSafe(name, attrName, decoded)

      const lower = attrName.toLowerCase()
      if (lower === 'href' || lower.endsWith(':href')) {
        // Normalized to a plain `href` — modern browsers resolve it on <use>
        // without an xlink namespace declaration, which we never emit.
        attributes.push(['href', decoded.trim()])
        sawHref = true
      } else if (ALLOWED_ATTRIBUTES.has(attrName)) {
        attributes.push([attrName, decoded])
      }
      // else: dropped (style, aria-*, data-*, xmlns:xlink, …)
    }
    if (name === 'use' && !sawHref) reject()

    const element: ParsedElement = { name, attributes, children: [] }
    if (selfClosing) return element

    for (;;) {
      if (this.i >= this.s.length) reject()
      if (this.startsWith('</')) {
        this.i += 2
        const closeName = this.readName()
        if (closeName !== name) reject()
        this.skipWhitespace()
        if (this.peek() !== '>') reject()
        this.i++
        return element
      }
      if (this.startsWith('<!--')) {
        const end = this.s.indexOf('-->', this.i + 4)
        if (end < 0) reject()
        this.i = end + 3
        continue
      }
      if (this.peek() === '<') {
        if (this.peek(1) === '!' || this.peek(1) === '?') reject()
        element.children.push(this.parseElement(depth + 1))
        continue
      }
      // Text node up to the next tag.
      const next = this.s.indexOf('<', this.i)
      const text = this.s.slice(this.i, next < 0 ? this.s.length : next)
      this.i += text.length
      if (name === 'title') {
        if (!TITLE_TEXT.test(text)) reject()
        element.children.push(text)
      } else if (text.trim() !== '') {
        reject()
      }
    }
  }
}

function prefixLocalRefs(value: string, prefix: string): string {
  if (!prefix) return value
  return value.replace(/url\(\s*(['"]?)#([A-Za-z_][A-Za-z0-9_.:-]*)\1\s*\)/g, (_m, q: string, id: string) => `url(${q}#${prefix}${id}${q})`)
}

function serialize(element: ParsedElement, prefix: string): string {
  const attrs = element.attributes
    .map(([name, value]) => {
      let out = value
      if (name === 'id') out = `${prefix}${value}`
      else if (name === 'href') out = `#${prefix}${value.slice(1)}`
      else out = prefixLocalRefs(value, prefix)
      return ` ${name}="${escapeAttr(out)}"`
    })
    .join('')
  if (element.children.length === 0) return `<${element.name}${attrs}/>`
  const inner = element.children.map((child) => (typeof child === 'string' ? child : serialize(child, prefix))).join('')
  return `<${element.name}${attrs}>${inner}</${element.name}>`
}

/**
 * Sanitizes one icon's SVG text. Returns null for ANY input that is not a
 * string, is over the size cap, is not well-formed, or contains anything
 * outside the allowlists described in the file header.
 */
export function sanitizeIconSvg(input: unknown, options: SanitizeIconSvgOptions = {}): SanitizedIconSvg | null {
  if (typeof input !== 'string') return null
  const text = input.replace(/^\uFEFF/, '')
  if (text.length === 0 || text.length > MAX_ICON_SVG_LENGTH) return null

  let root: ParsedElement
  try {
    root = new Tokenizer(text).parseDocument()
  } catch (error) {
    if (error instanceof Reject) return null
    throw error
  }

  const attr = (name: string) => root.attributes.find(([n]) => n === name)?.[1]
  let viewBox: string | null = null
  const rawViewBox = attr('viewBox')
  if (rawViewBox !== undefined) {
    viewBox = normalizeViewBox(rawViewBox)
  } else {
    const width = attr('width')
    const height = attr('height')
    if (width !== undefined && height !== undefined) viewBox = normalizeViewBox(`0 0 ${width} ${height}`)
  }
  if (!viewBox) return null

  const prefix = (options.idPrefix ?? '').replace(/[^A-Za-z0-9_-]/g, '')
  const markup = root.children.map((child) => (typeof child === 'string' ? child : serialize(child, prefix))).join('')
  if (!root.children.some((child) => typeof child !== 'string' && child.name !== 'title' && child.name !== 'defs')) {
    // Nothing drawable — a chip would render an empty 16px box.
    return null
  }

  return {
    viewBox,
    markup,
    svg: `<svg xmlns="${SVG_NAMESPACE}" viewBox="${viewBox}">${markup}</svg>`,
  }
}
